import React, { Suspense, useEffect, useRef } from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { I18nextProvider } from "react-i18next";
import { HelmetProvider } from "react-helmet-async";

import i18n from "./utils/i18n";
import getDesignTokens from "./utils/theme";
import Layout from "./components/Layout";
import RequireAuth from "./components/RequireAuth";
import ScrollToTop from "./components/ScrollToTop";
import Backdrop from "./components/common/Backdrop";
import ModalDialog from "./components/common/ModalDialog";
import Script from "./components/Script";
import HelmetJS from "./components/HelmetJS";
import useSyncRootScroll from "./components/useSyncRootScroll";
import useBankRegistrationGate from "./components/useBankRegistrationGate";

import Home from "./pages/Home";
import Homev2 from "./pages/Home_v2";
import Game from "./pages/Game";
import Game_v2 from "./pages/Game_v2";
import Bonus from "./pages/Bonus";
import Bonus_v2 from "./pages/Bonus_v2";
import Vip from "./pages/Vip";
import Vip2 from "./pages/Vip2";
import PromoCenter from "./pages/PromoCenter";
import RedemptionCode from "./pages/RedemptionCode";
import Affiliate from "./pages/Affiliate";
import Register from "./pages/Register";
import Login from "./pages/Login";
import About from "./pages/About";
import Terms from "./pages/Terms";
import Rules from "./pages/Rules";
import Faq from "./pages/Faq";
import Faq2 from "./pages/Faq2";
import LoginModal from "./pages/LoginModal";
import RegisterModal from "./pages/RegisterModal";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ContactUs from "./pages/ContactUs";
import Language from "./pages/Language";
import HowToJoin from "./pages/HowToJoin";
import HowToDeposit from "./pages/HowToDeposit";
import HowToWithdraw from "./pages/HowToWithdraw";
import ResponsibleGaming from "./pages/ResponsibleGaming";
import KYCVerification from "./pages/KYCVerification";
import AntiScam from "./pages/AntiScam";
import InfoCenter from "./pages/InfoCenter";
import ProfileWallet from "./pages/ProfileWallet";
import ProfileWallet2 from "./pages/ProfileWallet2";
import AddBankAccount from "./pages/AddBankAccount";
import Referral from "./pages/Referral";
import Blog from "./pages/Blog";
import BlogDetail from "./pages/BlogDetail";

import GoldenEgg from "./components/GoldenEgg";
import GameLauncher from "./components/Game/GameLauncher";
import PaymentGatewayLauncher from "./components/wallet/PaymentGatewayLauncher";
import GameRedirect from "./components/Game/GameRedirect";
import SpinWheelEmbed from "./components/common/SpinWheelEmbed";

import LiveChatPrefill from "./components/LiveChatPrefill";
import Messaging from "./pages/Messaging";

import { PageContentProvider } from "./contexts/PageContentContext.tsx";
import { MetaTags } from "./components/MetaTags.tsx";
import * as Constant from "./features/constant";

import Deposit from "./components/wallet/Deposit.js";
import Deposit2 from "./components/wallet/Deposit2.js";
import Withdrawal from "./components/wallet/Withdrawal.js";
import Withdrawal2 from "./components/wallet/Withdrawal2.js";
import MissionDiary from "./components/mission/MissionDiary.jsx";
import Transaction from "./components/wallet/Transaction.js";
import BetRecord from "./components/wallet/BetRecord.js";
import PromotionRecord from "./components/wallet/PromotionRecord.js";
import UserProfile from "./components/wallet/UserProfile.js";
import BankAccount from "./components/wallet/BankAccount.js";
import ChangePassword from "./components/wallet/ChangePassword.js";
import OngoingBonus from "./components/wallet/OngoingBonus.js";
import DepositMobile from "./components/wallet/DepositMobile.js";
import Deposit3 from "./components/wallet/Deposit3.js";
import History from "./components/wallet/History.js";
function App() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const showLoginModal = queryParams.get("login") === "1";
  const showRegisterModal = queryParams.get("register") === "1";

  const theme = createTheme(getDesignTokens());
  useSyncRootScroll();

  // Bank-registration gate. Enforced here at render time (not via an effect) so
  // that no child route/component can navigate past it in a race — returning
  // <Navigate> early means the gated tree (Home, games, launchers) never mounts.
  const { mustAddBank, shouldLeaveAddBankPage, gatePending, addBankPath } =
    useBankRegistrationGate();

  // Last committed pathname — lets the gate tell "user tried to leave the
  // add-bank page" (show popup) apart from the initial redirect after login.
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    prevPathRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    if (!process.env.REACT_APP_LANG_PATH_PARAM) return;

    const LANG_KEYS = [
      process.env.REACT_APP_DEFAULT_LANGUAGE,
      process.env.REACT_APP_SECOND_LANGUAGE,
      process.env.REACT_APP_THIRD_LANGUAGE,
      process.env.REACT_APP_FOURTH_LANGUAGE,
    ].filter(Boolean);

    const LANG_PATHS = [
      process.env.REACT_APP_LANG_PATH_FIRSTLANG,
      process.env.REACT_APP_LANG_PATH_SECONDLANG,
      process.env.REACT_APP_LANG_PATH_THIRDLANG,
      process.env.REACT_APP_LANG_PATH_FOURTHLANG,
    ].filter(Boolean);

    const URL_LANG_MAP = LANG_PATHS.reduce((acc, path, idx) => {
      acc[path] = LANG_KEYS[idx];
      return acc;
    }, {});

    let pathname = location.pathname;
    const search = location.search;

    // Detect language prefix
    const match = pathname.match(/^\/([^/]+)(\/|$)/);
    const urlLang = match?.[1];

    if (urlLang && URL_LANG_MAP[urlLang]) {
      // Valid language in URL
      const detectedLang = URL_LANG_MAP[urlLang];

      if (i18n.language !== detectedLang) i18n.changeLanguage(detectedLang);
      if (localStorage.getItem("language") !== detectedLang)
        localStorage.setItem("language", detectedLang);
    } else {
      // Redirect only if URL does not already start with correct prefix
      const storedLang = localStorage.getItem("language") || process.env.REACT_APP_DEFAULT_LANGUAGE;
      const redirectLang = LANG_PATHS[LANG_KEYS.indexOf(storedLang)] || LANG_PATHS[0];

      if (!pathname.startsWith(`/${redirectLang}`)) {
        window.location.replace(`/${redirectLang}${pathname}${search}`);
        return;
      }
    }

    // Add trailing slash if URL is a language root
    const langRootMatch = pathname.match(/^\/([^/]+)$/);
    if (langRootMatch) {
      const newPath = `${pathname}/`;
      if (newPath !== pathname) {
        window.history.replaceState(null, "", `${newPath}${search}`);
        pathname = newPath;
      }
    }

    // Clean double slashes
    const cleanPathname = pathname.replace(/\/{2,}/g, "/");
    if (cleanPathname !== pathname) {
      window.history.replaceState(null, "", `${cleanPathname}${search}`);
    }
  }, [location.pathname]);

  const getRoutePath = (path) => {
    return process.env.REACT_APP_LANG_PATH_PARAM
      ? "/:lang/" + path
      : path;
  };

  if (mustAddBank) {
    const leftAddBankPage = prevPathRef.current.includes("addbankaccount");
    return (
      <Navigate
        to={addBankPath}
        replace
        state={leftAddBankPage ? { bankRequired: true } : undefined}
      />
    );
  }
  if (shouldLeaveAddBankPage) {
    return <Navigate to={Constant.getPath("/")} replace />;
  }

  const isGameLauncher = location.pathname.includes("gameLauncher");
  const isPaymentGatewayLauncher = location.pathname.includes("paymentGatewayLauncher");

  // Hold the game / payment launcher until the bank gate has actually decided.
  // A cold full-page load straight to /gameLauncher would otherwise render the
  // game iframe before the async bank check can redirect a bankless user out.
  if ((isGameLauncher || isPaymentGatewayLauncher) && gatePending) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Backdrop />
      </ThemeProvider>
    );
  }

  if (isGameLauncher) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route
            path={getRoutePath("gameLauncher")}
            element={<GameLauncher />}
          />
        </Routes>
      </ThemeProvider>
    );
  }

  if (isPaymentGatewayLauncher) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Routes>
          <Route
            path={getRoutePath("paymentGatewayLauncher")}
            element={<PaymentGatewayLauncher />}
          />
        </Routes>
      </ThemeProvider>
    );
  }

  return (
    <HelmetProvider>
      <I18nextProvider i18n={i18n}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <HelmetJS />
            <Script />
            <ScrollToTop />
            <ModalDialog />
            <LiveChatPrefill />


            <PageContentProvider>
              <MetaTags />
              <Suspense fallback={<Backdrop />}>
                <Routes>
                  <Route element={<Layout />}>
                    <Route path="*" element={process.env.REACT_APP_HOME_V2 === 'true' ? <Homev2 /> : <Home />}  />
                    <Route path={getRoutePath("game/:gameCategoryCode")} exact element={process.env.REACT_APP_GAME_V2 === 'true' ? <Game_v2 /> : <Game />} />
                    <Route path={getRoutePath("game/:gameCategoryCode/:gameProviderCode")} exact element={process.env.REACT_APP_GAME_V2 === 'true' ? <Game_v2 /> : <Game />} />
                    <Route path={getRoutePath("promotion")} exact element={
                      process.env.REACT_APP_BONUS_V2 === 'true' ? <Bonus_v2 /> : <Bonus />
                    } />
                    <Route path={getRoutePath("promocenter")} exact element={<PromoCenter />} />
                    <Route path={getRoutePath("affiliate")} exact element={<Affiliate />} />
                    <Route path={getRoutePath("register")} exact element={<Register />} />
                    <Route path={getRoutePath("login")} exact element={<Login />} />
                    {/* <Route path={getRoutePath("vip")} exact element={<Vip />} /> */}
                    <Route path={getRoutePath("vip")} exact element={<Vip2 />} />
                    <Route path={getRoutePath("about")} exact element={<About />} />
                    <Route path={getRoutePath("terms")} exact element={<Terms />} />
                    <Route path={getRoutePath("rules")} exact element={<Rules />} />
                    <Route path={getRoutePath("faq")} exact element={process.env.REACT_APP_USE_FAQ2 === 'true' ? <Faq2 /> : <Faq />} />
                    <Route path={getRoutePath("contactus")} exact element={<ContactUs />} />
                    <Route path={getRoutePath("language")} exact element={<Language />} />
                    <Route path={getRoutePath("howToJoin")} exact element={<HowToJoin />} />
                    <Route path={getRoutePath("howToDeposit")} exact element={<HowToDeposit />} />
                    <Route path={getRoutePath("howToWithdraw")} exact element={<HowToWithdraw />} />
                    <Route path={getRoutePath("responsibleGaming")} exact element={<ResponsibleGaming />} />
                    <Route path={getRoutePath("kycVerification")} exact element={<KYCVerification />} />
                    <Route path={getRoutePath("antiScam")} exact element={<AntiScam />} />
                    <Route path={getRoutePath("forgotPassword")} exact element={<ForgotPassword />} />
                    <Route path={getRoutePath("resetPassword")} exact element={<ResetPassword />} />
                    <Route path={getRoutePath("infoCenter")} exact element={<InfoCenter />} />
                    <Route path={getRoutePath("goldenegg")} exact element={<GoldenEgg />} />
                    <Route path={getRoutePath("deposit")} exact element={process.env.REACT_APP_DEPOSIT_VERSION === "Deposit3" ? <Deposit3 /> : <DepositMobile />} />
                    <Route path={getRoutePath("withdrawal")} exact element={process.env.REACT_APP_WITHDRAWAL_VERSION === "Withdrawal2" ? <Withdrawal2 /> : <Withdrawal />} />
                    <Route path={getRoutePath("mission")} exact element={<MissionDiary />} />
                    <Route path={getRoutePath("ongoingbonus")} exact element={<OngoingBonus />} />
                    <Route path={getRoutePath("history")} exact element={<History />} />
                    <Route path={getRoutePath("transaction")} exact element={<Transaction />} />
                    <Route path={getRoutePath("betrecord")} exact element={<BetRecord />} />
                    <Route path={getRoutePath("promorecord")} exact element={<PromotionRecord />} />
                    <Route path={getRoutePath("myaccount")} exact element={<UserProfile />} />
                    <Route path={getRoutePath("bankaccount")} exact element={<BankAccount />} />
                    <Route path={getRoutePath("changepassword")} exact element={<ChangePassword />} />

                    <Route element={<RequireAuth />}>
                      <Route path={getRoutePath("profileWallet/:tab?")} element={process.env.REACT_APP_PROFILE_WALLET_VERSION === "ProfileWallet2" ? <ProfileWallet2 /> : <ProfileWallet />} />
                      {process.env.REACT_APP_BANK_REGISTER === "true" && (
                        <Route path={getRoutePath("addbankaccount")} element={<AddBankAccount />} />
                      )}
                      <Route path={getRoutePath("messaging")} element={<Messaging />} />
                      {/* Login required - the redeem endpoint is ROLE_PLAYER gated, so a
                          logged-out visitor could read the page but never submit. */}
                      <Route path={getRoutePath("redemptioncode")} element={<RedemptionCode />} />
                    </Route>

                    <Route path={getRoutePath("referral")} element={<Referral />} />
                    {process.env.REACT_APP_SHOW_BLOG === "true" && (
                      <>
                        <Route path={getRoutePath("blog")} element={<Blog />} />
                        <Route path={getRoutePath("blog/:slug")} element={<BlogDetail />} />
                      </>
                    )}
                    <Route path={getRoutePath("goldenegg")} element={<GoldenEgg />} />
                  </Route >

                  <Route path={getRoutePath("gameRedirect")} element={<GameRedirect />} />
                  <Route path={getRoutePath("spinWheel")} element={<SpinWheelEmbed />} />
                </Routes >
                {showLoginModal && <LoginModal />
                }
                {showRegisterModal && <RegisterModal />}
              </Suspense >
            </PageContentProvider >
          </ThemeProvider >
        </LocalizationProvider >
      </I18nextProvider >
    </HelmetProvider >
  );
}

export default App;
